import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { BatchSourcingDefaultService } from './batch-sourcing-default.service';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-batch-sourcing-default',
    templateUrl: './batch-sourcing-default.component.html',
    styleUrls: ['./batch-sourcing-default.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class BatchSourcingDefaultComponent implements OnInit, OnDestroy {

	baseGlobalParams: {
    jcd_key?: string | number;
    tax_year?: string | number;
    scenario?: string | number;
    client_key?: string | number;
    scenarioCode?: string;
    screen_key?: string | number;
    filing_key?: string | number;
    checkLockedbj?: any;
    modalScreen?: { modal_name?: string };
  } | undefined;
  	baseURLs: any ={'admin':'','api':'/gtw',"custom":'','efile':'','pdfEngine':'','bulkPdfEngine':''};
	taxYear :number = 0;
	scenario :number = 0;
	jcdKey :number = 0;
	screenKey :number = 0;
	filingKey :number = 0;
	acctKey :number = 0;
	ssoId: string = '';
	acctType:string = '';
	parentMeKey :number = 0;
	scenarioCode: string = '';
	checkLockedObj: any = [];
	entities : any = [];
	basket_id_value: any;
	saveClicked = false;
	minimise = false;

	@Output('close-modal')
	closeModal = new EventEmitter<string>();

	@Output('request-grid-update')
	requestGridUpdate = new EventEmitter<string>();

	subscriptions: Subscription = new Subscription();

	constructor(
    private BatchSourcingDefaultService: BatchSourcingDefaultService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

	ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subscriptions.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
    }));
    this.subscriptions.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
    }));
	}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Normalize gizmoGrid batch rows to `{ row_id?, data }` entries. */
  private normalizeBatchRows(rows: any[]): any[] {
    return rows
      .map((r) => (r?.data !== undefined ? r : { data: r }))
      .filter((r) => r?.data != null);
  }

  private resolveBatchRows(activeScreen: any): any[] {
    const gridData = gridPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(gridData) && gridData.length) {
      return this.normalizeBatchRows(gridData);
    }

    const batchSelect = activeScreen?.data?.batchSelect;
    if (Array.isArray(batchSelect) && batchSelect.length) {
      return this.normalizeBatchRows(batchSelect);
    }

    const selected = activeScreen?.data?.selected;
    if (Array.isArray(selected) && selected.length) {
      return this.normalizeBatchRows(selected);
    }

    const rowData = activeScreen?.data?.rowData;
    if (rowData && typeof rowData === 'object' && !Array.isArray(rowData)) {
      const keys = Object.keys(rowData);
      const firstVal = rowData[keys[0]];
      if (firstVal && typeof firstVal === 'object' && firstVal.data) {
        return this.normalizeBatchRows(Object.values(rowData));
      }
      return this.normalizeBatchRows([rowData]);
    }

    return [];
  }

  private entityRowKey(row: any): string {
    const d = row?.data ?? row ?? {};
    return String(
      row?.row_id
      ?? d.object_id
      ?? d.OBJECT_ID
      ?? `${d.ME_CODE ?? ''}|${d.ACCOUNT ?? ''}|${d.PARENT_ME_NAME ?? ''}`
    );
  }

  private buildCheckLockedObj(rows: any[]): void {
    const locks = rows
      .map((row) => {
        const data = row?.data ?? row ?? {};
        const meCode = data.ME_CODE;
        if (meCode == null || meCode === '') {
          return null;
        }
        return {
          me_code: meCode,
          consol_group_key: this.filingKey,
          scenario_key: this.scenario,
          tax_year: this.taxYear,
          scenarioCode: this.scenarioCode,
          is_issue_key_enabled: 'N',
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry != null);

    this.checkLockedObj = _.uniqBy(locks, (l) => l.me_code);
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.baseGlobalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    this.taxYear = Number(this.baseGlobalParams?.tax_year || this.taxYear || 0);
    this.scenario = Number(this.baseGlobalParams?.scenario || this.scenario || 0);
    this.jcdKey = Number(this.baseGlobalParams?.jcd_key || this.jcdKey || 0);
    this.screenKey = Number(this.baseGlobalParams?.screen_key || this.screenKey || 0);
    this.filingKey = Number(this.baseGlobalParams?.filing_key || this.filingKey || 0);
    this.scenarioCode = String(
      this.baseGlobalParams?.scenarioCode
      ?? this.gtwGlobal?.global?.globalParams?.scenarioCode
      ?? this.scenarioCode
      ?? ''
    );
    this.ssoId = this.gtwApp?.USER_SETTINGS?.user?.sso_id || this.ssoId;

    const rows = this.resolveBatchRows(activeScreen);
    if (rows.length) {
      this.entities = _.uniqBy(rows, (r) => this.entityRowKey(r));
      this.buildCheckLockedObj(this.entities);
    } else if (this.baseGlobalParams?.checkLockedbj) {
      this.checkLockedObj = this.baseGlobalParams.checkLockedbj;
    }
  }

	saveData(): void {
		this.saveClicked = true;

		const selectedMeCodes = this.entities
      .map((value: any) => value?.data?.ME_CODE ?? value?.ME_CODE)
      .filter((code: any) => code != null && code !== '');

	   	const tempData = {
			TAX_YEAR : this.baseGlobalParams?.tax_year,
			SCENARIO : this.baseGlobalParams?.scenario,
			JCD_KEY : this.baseGlobalParams?.jcd_key,
			SSO_ID: this.ssoId,
			ME_KEY : this.parentMeKey,
			ACCT_KEY : this.acctKey,
			FILING_KEY : this.filingKey
	   	};

	   	const updateSubscription = this.BatchSourcingDefaultService.saveSourcingInfoData(
        this.baseURLs.api,
        tempData,
        this.baseGlobalParams,
        this.checkLockedObj,
        _.uniq(selectedMeCodes)
      ).subscribe(
			(data) => {
			   	if (data.callSuccess !== '1')  {
					this.saveClicked = false;
					this.BatchSourcingDefaultService.openSnackBar(
            'Error Saving Data. ' + (data?.errorMessage ?? ''),
            'gtw-snackbar--red'
          );
				} else {
					this.saveClicked = false;
          this.BatchSourcingDefaultService.openSnackBar(
            'The default sourcing will be regenerated for selected businesses',
            'gtw-snackbar--green'
          );
          this.requestGridUpdate.emit('refresh');
				}
			},
      () => {
				this.saveClicked = false;
        this.BatchSourcingDefaultService.openSnackBar(
          'Error Saving Data.',
          'gtw-snackbar--red'
        );
			}
		  );
		this.subscriptions.add(updateSubscription);
	}

	removeEntity(_index: number): void {
		this.entities.splice(_index, 1);
		if (this.entities.length === 0) {
			this.modalClose();
		}
	}

	modalClose(): void {
		this.closeModal.emit();
	}
}
